import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import RoleNavigation from '../../components/ui/RoleNavigation';
import CardMockup from './components/CardMockup';
import CardBenefits from './components/CardBenefits';
import WaitlistModal from './components/WaitlistModal';
import WalletFlowPreview from './components/WalletFlowPreview';
import EligibilityIndicator from './components/EligibilityIndicator';
import FutureFeatures from './components/FutureFeatures';
import Icon from '../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const SocialCentsCardScreen = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const [loadingScore, setLoadingScore] = useState(true);

  const loadScore = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await supabase
        ?.from('scores')
        ?.select('total_score')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();
      if (data) setScore(data?.total_score || 0);
    } catch (err) {
      console.error('[SocialCentsCard] loadScore error:', err);
    } finally {
      setLoadingScore(false);
    }
  }, [user?.id]);

  const checkWaitlist = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await supabase
        ?.from('card_waitlist')
        ?.select('id')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();
      if (data) setAlreadyJoined(true);
    } catch (err) {
      console.error('[SocialCentsCard] checkWaitlist error:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    loadScore();
    checkWaitlist();
    window.scrollTo(0, 0);
  }, [user?.id]);

  const handleWaitlistSuccess = () => {
    setAlreadyJoined(true);
  };

  return (
    <>
      <Helmet>
        <title>SocialCents Card — SocialCents</title>
      </Helmet>

      <RoleNavigation role={userProfile?.role || 'influencer'} userName={userProfile?.full_name || user?.email} />

      <div className="min-h-screen pt-[60px]" style={{ background: '#0F172A' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-10">

          {/* Hero Section */}
          <div
            className="rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(13,148,136,0.12) 0%, rgba(29,78,216,0.12) 50%, rgba(5,150,105,0.08) 100%)',
              border: '1px solid rgba(20, 184, 166, 0.15)'
            }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left: Card visual */}
              <div className="flex items-center justify-center p-8">
                <CardMockup />
              </div>

              {/* Right: Intro text */}
              <div className="flex flex-col justify-center p-8 lg:pl-4">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-4 w-fit"
                  style={{ background: 'rgba(20,184,166,0.12)', color: '#14B8A6', border: '1px solid rgba(20,184,166,0.25)' }}
                >
                  <Icon name="Sparkles" size={12} />
                  Coming Soon
                </div>

                <h1 className="text-3xl font-bold text-foreground mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  The SocialCents Card
                </h1>
                <p className="text-muted-foreground text-base leading-relaxed mb-6">
                  A payment card built for creators. Earn cashback automatically on every purchase — no manual submissions, no friction.
                </p>

                {/* Status banner */}
                <div
                  className="flex items-start gap-3 p-4 rounded-xl mb-6"
                  style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)' }}
                >
                  <Icon name="Construction" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    The SocialCents Card is currently under development and will soon allow influencers to earn rewards automatically from their purchases.
                  </p>
                </div>

                {/* CTA */}
                {alreadyJoined ? (
                  <div className="flex flex-col gap-3">
                    <div
                      className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold w-fit"
                      style={{ background: 'rgba(20,184,166,0.12)', color: '#14B8A6', border: '1px solid rgba(20,184,166,0.25)' }}
                    >
                      <Icon name="CheckCircle" size={16} />
                      You're on the waitlist!
                    </div>
                    <button
                      onClick={() => navigate('/card-application-form-screen')}
                      className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white w-fit transition-all duration-200 hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
                      style={{ background: 'linear-gradient(135deg, #0d9488, #1d4ed8)' }}
                    >
                      <Icon name="FileText" size={16} />
                      Apply for Card
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setWaitlistOpen(true)}
                      className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white w-fit transition-all duration-200 hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
                      style={{ background: 'linear-gradient(135deg, #0d9488, #1d4ed8)' }}
                    >
                      <Icon name="Sparkles" size={16} />
                      Join Early Access Waitlist
                    </button>
                    <button
                      onClick={() => navigate('/card-application-form-screen')}
                      className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
                      style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.12)' }}
                    >
                      <Icon name="FileText" size={16} />
                      Apply for Card
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Benefits */}
          <CardBenefits />

          {/* Wallet Flow */}
          <WalletFlowPreview />

          {/* Eligibility */}
          {!loadingScore && (
            <EligibilityIndicator score={score} />
          )}

          {/* Future Features */}
          <FutureFeatures />

          {/* Check Status CTA */}
          <div
            className="rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(20,184,166,0.12)', border: '1px solid rgba(20,184,166,0.25)' }}
              >
                <Icon name="ClipboardCheck" size={16} color="#14B8A6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Already Applied?</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Track your application progress in real-time</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/card-application-status-screen')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 hover:scale-[1.01] active:scale-[0.99]"
              style={{ background: 'rgba(20,184,166,0.12)', color: '#14B8A6', border: '1px solid rgba(20,184,166,0.25)' }}
            >
              <Icon name="ArrowRight" size={14} />
              Check Application Status
            </button>
          </div>

          {/* Bottom CTA */}
          {!alreadyJoined && (
            <div
              className="rounded-2xl p-8 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(13,148,136,0.1) 0%, rgba(29,78,216,0.1) 100%)',
                border: '1px solid rgba(20, 184, 166, 0.15)'
              }}
            >
              <h3 className="text-xl font-semibold text-foreground mb-2">Ready to be first in line?</h3>
              <p className="text-muted-foreground text-sm mb-6">Join the waitlist now. High-performing creators get priority access.</p>
              <button
                onClick={() => setWaitlistOpen(true)}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #0d9488, #1d4ed8)' }}
              >
                <Icon name="Sparkles" size={16} />
                Join Early Access Waitlist
              </button>
            </div>
          )}
        </div>
      </div>

      <WaitlistModal
        isOpen={waitlistOpen}
        onClose={() => {
          setWaitlistOpen(false);
          checkWaitlist();
        }}
        user={user}
        userProfile={userProfile}
      />
    </>
  );
};

export default SocialCentsCardScreen;
